import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, Grid, InputLabel, ListSubheader, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { formatBudgetCurrency, procedureDisplayPrice } from '../budget/budgetFormModel';
import {
  DENTAL_CONDITIONS,
  DENTAL_CONDITION_GROUPS,
  DENTAL_ENTRY_STATUSES,
  TOOTH_FACES,
  getCondition,
  getFaceLabel,
  getToothName,
  type ToothFace,
} from '../../constants/odontology';
import type { DentalChartEntry, DentalEntryStatus } from '../../types/odontology';
import { useAuth } from '../../contexts/AuthContext';
import { createDentalChartEntry, updateDentalChartEntry } from '../../services/odontologyService';
import { useDentalProfessionals } from './odontologyHooks';

export interface DentalChartEntryDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  unitId?: string | null;
  chartId?: string | null;
  toothNumber: string | null;
  /** Faces pré-selecionadas no odontograma ao abrir o formulário. */
  initialFaces?: ToothFace[];
  entry?: DentalChartEntry | null;
  onSaved: () => void;
}

interface FormState {
  entry_type: 'condition' | 'procedure' | 'note';
  condition_code: string;
  faces: ToothFace[];
  status: DentalEntryStatus;
  procedure_id: string;
  procedure_name: string;
  description: string;
  amount: string;
  professional_id: string;
  planned_date: string;
  performed_date: string;
}

const emptyForm = (faces: ToothFace[], professionalId = ''): FormState => ({
  entry_type: 'procedure',
  condition_code: '',
  faces,
  status: 'planned',
  procedure_id: '',
  procedure_name: '',
  description: '',
  amount: '',
  professional_id: professionalId,
  planned_date: '',
  performed_date: '',
});

const DentalChartEntryDialog: React.FC<DentalChartEntryDialogProps> = ({
  open,
  onClose,
  patientId,
  unitId,
  chartId,
  toothNumber,
  initialFaces = [],
  entry,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm(initialFaces));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [procedureSearch, setProcedureSearch] = useState('');
  const [procedureOptions, setProcedureOptions] = useState<any[]>([]);
  const [selectedProcedure, setSelectedProcedure] = useState<any | null>(null);
  const { user } = useAuth();
  const loggedProfessionalId = user?.professional_id || '';
  const canSelectProfessional = !loggedProfessionalId;
  const professionals = useDentalProfessionals(open);

  const loggedProfessionalName = useMemo(() => {
    if (!loggedProfessionalId) return '';
    const fromList = professionals.find((p) => p.id === loggedProfessionalId)?.full_name;
    return fromList || user?.full_name || 'Profissional logado';
  }, [loggedProfessionalId, professionals, user?.full_name]);

  const facesRef = useRef<ToothFace[]>(initialFaces);
  facesRef.current = initialFaces;

  const isEdit = Boolean(entry?.id);

  // Reinicia o formulário apenas ao abrir ou ao trocar de lançamento; `initialFaces`
  // é lido como valor inicial, não como dependência.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setProcedureSearch('');
    if (entry) {
      setForm({
        entry_type: entry.entry_type,
        condition_code: entry.condition_code || '',
        faces: (entry.faces || []) as ToothFace[],
        status: entry.status,
        procedure_id: entry.procedure_id || '',
        procedure_name: entry.procedure_name || '',
        description: entry.description || '',
        amount: entry.amount != null ? String(entry.amount) : '',
        professional_id: entry.professional_id || '',
        planned_date: entry.planned_date || '',
        performed_date: entry.performed_date || '',
      });
      setSelectedProcedure(
        entry.procedure_id
          ? { id: entry.procedure_id, name: entry.procedure_name || '' }
          : null,
      );
    } else {
      setForm(emptyForm(facesRef.current, loggedProfessionalId));
      setSelectedProcedure(null);
    }
  }, [open, entry?.id, loggedProfessionalId]);

  useEffect(() => {
    if (!open || !entry?.procedure_id) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`/clinic/procedures/${entry.procedure_id}`));
        const proc = res.data?.data;
        if (!active || !proc?.id) return;
        setSelectedProcedure(proc);
        setProcedureOptions((prev) => (prev.some((p) => p.id === proc.id) ? prev : [proc, ...prev]));
      } catch {
        /* mantém stub com id + nome */
      }
    })();
    return () => { active = false; };
  }, [open, entry?.procedure_id]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/budgets-search/procedures'), {
          params: { search: procedureSearch || '' },
        });
        if (res.data?.success !== false) setProcedureOptions(res.data?.data || []);
        else setProcedureOptions([]);
      } catch {
        setProcedureOptions([]);
      }
    }, procedureSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [procedureSearch, open]);

  const condition = getCondition(form.condition_code);

  // Condições de dente inteiro (coroa, implante, ausência) não aceitam face.
  const facesEnabled = condition?.scope !== 'tooth';

  const conditionsByGroup = useMemo(
    () => DENTAL_CONDITION_GROUPS.map((group) => ({
      group,
      options: DENTAL_CONDITIONS.filter((c) => c.group === group),
    })),
    [],
  );

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleFace = (face: ToothFace) => {
    setForm((prev) => ({
      ...prev,
      faces: prev.faces.includes(face) ? prev.faces.filter((f) => f !== face) : [...prev.faces, face],
    }));
  };

  const selectProcedure = (proc: any | null) => {
    setSelectedProcedure(proc);
    if (!proc?.id) {
      setForm((prev) => ({ ...prev, procedure_id: '', procedure_name: '' }));
      return;
    }
    const price = procedureDisplayPrice(proc);
    setForm((prev) => ({
      ...prev,
      procedure_id: proc.id,
      procedure_name: proc.name || '',
      amount: prev.amount && Number(String(prev.amount).replace(',', '.')) > 0
        ? prev.amount
        : (price ? String(price) : ''),
    }));
  };

  const handleSave = async () => {
    if (!toothNumber) return;
    if (!form.procedure_id) {
      setError('Selecione um procedimento do catálogo.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        patient_id: patientId,
        unit_id: unitId || undefined,
        chart_id: chartId || undefined,
        tooth_number: toothNumber,
        entry_type: 'procedure' as const,
        condition_code: form.condition_code || null,
        faces: facesEnabled ? form.faces : [],
        status: form.status,
        procedure_id: form.procedure_id || null,
        procedure_name: form.procedure_name.trim() || null,
        description: form.description.trim() || null,
        amount: form.amount ? Number(form.amount.replace(',', '.')) : 0,
        professional_id: loggedProfessionalId || form.professional_id || null,
        planned_date: form.planned_date || null,
        performed_date: form.performed_date || null,
      };

      if (isEdit && entry) await updateDentalChartEntry(entry.id, payload);
      else await createDentalChartEntry(payload);

      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao salvar lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
          {isEdit ? 'Editar procedimento' : 'Lançar procedimento'}
        </Typography>
        {toothNumber && (
          <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25 }}>
            Dente {toothNumber} — {getToothName(toothNumber)}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              options={procedureOptions}
              value={selectedProcedure}
              onChange={(_, proc) => selectProcedure(proc)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setProcedureSearch(value);
              }}
              onOpen={() => { if (procedureOptions.length === 0) setProcedureSearch(''); }}
              filterOptions={(x) => x}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              getOptionLabel={(o) => `${o?.name || ''}${o?.code ? ` (${o.code})` : ''}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Procedimento"
                  placeholder="Buscar no catálogo da clínica…"
                />
              )}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box sx={{ py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {o.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      {[
                        o.code,
                        o.category?.name || o.clinic_procedure_categories?.name,
                        formatBudgetCurrency(procedureDisplayPrice(o)),
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText="Nenhum procedimento encontrado"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="dental-condition-label">Condição clínica</InputLabel>
              <Select
                labelId="dental-condition-label"
                label="Condição clínica"
                value={form.condition_code}
                onChange={(e) => setField('condition_code', e.target.value)}
              >
                <MenuItem value="">Nenhuma</MenuItem>
                {conditionsByGroup.flatMap(({ group, options }) => [
                  <ListSubheader key={group} sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94A3B8' }}>
                    {group}
                  </ListSubheader>,
                  ...options.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: option.color, border: '1px solid #CBD5E1' }} />
                        {option.label}
                      </Box>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mb: 0.75 }}>
              Faces {condition?.scope === 'tooth' && '(esta condição aplica ao dente inteiro)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {TOOTH_FACES.map((face) => (
                <Chip
                  key={face}
                  label={getFaceLabel(face, toothNumber || undefined)}
                  size="small"
                  disabled={!facesEnabled}
                  onClick={() => toggleFace(face)}
                  variant={form.faces.includes(face) ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600,
                    bgcolor: form.faces.includes(face) ? '#0F172A' : 'transparent',
                    color: form.faces.includes(face) ? '#FFFFFF' : '#475569',
                    '&:hover': { bgcolor: form.faces.includes(face) ? '#1E293B' : '#F1F5F9' },
                  }}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="dental-status-label">Status</InputLabel>
              <Select
                labelId="dental-status-label"
                label="Status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value as DentalEntryStatus)}
              >
                {DENTAL_ENTRY_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            {canSelectProfessional ? (
              <FormControl fullWidth size="small">
                <InputLabel id="dental-professional-label">Profissional</InputLabel>
                <Select
                  labelId="dental-professional-label"
                  label="Profissional"
                  value={form.professional_id}
                  onChange={(e) => setField('professional_id', e.target.value)}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  {professionals.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Profissional"
                value={loggedProfessionalName}
                InputProps={{ readOnly: true }}
                helperText="Vinculado ao usuário logado"
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data planejada"
              InputLabelProps={{ shrink: true }}
              value={form.planned_date}
              onChange={(e) => setField('planned_date', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data de realização"
              InputLabelProps={{ shrink: true }}
              value={form.performed_date}
              onChange={(e) => setField('performed_date', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Valor (R$)"
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              placeholder="0,00"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Observações"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !toothNumber}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DentalChartEntryDialog;
